/**
 * UbpFiltriCascade — Shared cascading filter logic for [comp="filtri-livello1"]
 *
 * After any filter change, hides panel items and bar buttons that would produce
 * zero results. Used by both archivio-edizioni and archive-eventi pages.
 *
 * Usage:
 *   var cascade = UbpFiltriCascade.create({ root, getItems, panels, barMatchFn, ... });
 *   cascade.update();  // call after every filter change
 */
var UbpFiltriCascade = (function () {

  /**
   * Helper: check if a space-separated class string contains a given class
   */
  function hasClass(classString, cls) {
    return (' ' + classString + ' ').indexOf(' ' + cls + ' ') !== -1;
  }

  /**
   * Create a cascade instance.
   *
   * @param {Object} config
   * @param {Element}   config.root          - The [comp="filtri-livello1"] element
   * @param {Function}  config.getItems      - Returns array of { classes: string, attrs: {} }
   * @param {Object}    config.panels        - Map of panelId → { matchFn(item, value) → bool }
   * @param {Function}  config.barMatchFn    - (item, filterValue) → bool
   * @param {Function}  [config.itemFilter]  - Extra filter on items (e.g. map view excludes eventi)
   * @param {Function}  [config.onPanelChanged] - Called with (panel) when items auto-deselected
   * @param {Function}  [config.onBarUpdate]    - Called with (activeWasHidden) after bar buttons updated
   * @param {boolean}   [config.updateBar=true] - Whether to cascade bar buttons
   */
  function create(config) {
    var root = config.root;
    var panelIds = Object.keys(config.panels);

    function update() {
      var items = config.getItems();
      if (!items || items.length === 0) return;

      // Current bar filter
      var activeBtn = root.querySelector('.btn[data-filter].--active');
      var barFilter = activeBtn ? activeBtn.getAttribute('data-filter') : '*';

      // Collect current selections from all panels
      var panelSelections = {};
      panelIds.forEach(function (pid) {
        var panel = document.getElementById(pid);
        if (!panel) return;
        var selected = [];
        panel.querySelectorAll('.grid-item.--selected').forEach(function (gi) {
          selected.push(gi.getAttribute('data-value'));
        });
        panelSelections[pid] = selected;
      });

      // ── Cascade each panel ──
      panelIds.forEach(function (currentPanelId) {
        var currentPanel = document.getElementById(currentPanelId);
        if (!currentPanel) return;
        var currentMatchFn = config.panels[currentPanelId].matchFn;

        // Find items matching bar + ALL OTHER panels (not current)
        var matchingItems = items.filter(function (item) {
          if (config.itemFilter && !config.itemFilter(item)) return false;
          if (!config.barMatchFn(item, barFilter)) return false;

          for (var pid in panelSelections) {
            if (pid === currentPanelId) continue;
            var vals = panelSelections[pid];
            if (!vals || vals.length === 0) continue;

            var panelMatchFn = config.panels[pid].matchFn;
            var anyMatch = false;
            for (var i = 0; i < vals.length; i++) {
              if (panelMatchFn(item, vals[i])) { anyMatch = true; break; }
            }
            if (!anyMatch) return false;
          }
          return true;
        });

        // Check each grid-item availability
        var changed = false;
        currentPanel.querySelectorAll('.grid-item').forEach(function (gridItem) {
          var value = gridItem.getAttribute('data-value');
          var available = matchingItems.some(function (item) {
            return currentMatchFn(item, value);
          });

          if (available) {
            gridItem.style.display = '';
          } else {
            gridItem.style.display = 'none';
            if (gridItem.classList.contains('--selected')) {
              gridItem.classList.remove('--selected');
              changed = true;
            }
          }
        });

        if (changed && config.onPanelChanged) {
          config.onPanelChanged(currentPanel);
        }
      });

      // ── Hide dropdown buttons for panels with 0 visible items ──
      panelIds.forEach(function (pid) {
        var panel = document.getElementById(pid);
        if (!panel) return;
        var visibleCount = panel.querySelectorAll('.grid-item:not([style*="display: none"])').length;
        var ddBtn = root.querySelector('.dropdown[data-panel="' + pid + '"]');
        if (ddBtn) {
          ddBtn.style.display = visibleCount > 0 ? '' : 'none';
          // If hidden and panel was open, close it
          if (visibleCount === 0 && !panel.hidden) {
            panel.hidden = true;
            ddBtn.classList.remove('is-open');
          }
        }
      });

      // ── Cascade bar buttons ──
      if (config.updateBar !== false) {
        updateBarButtons(config, items, panelSelections);
      }
    }

    function updateBarButtons(cfg, items, panelSelections) {
      // Find items matching all panel selections (ignoring bar)
      var matchingItems = items.filter(function (item) {
        if (cfg.itemFilter && !cfg.itemFilter(item)) return false;

        for (var pid in panelSelections) {
          var vals = panelSelections[pid];
          if (!vals || vals.length === 0) continue;
          var panelMatchFn = cfg.panels[pid].matchFn;
          var anyMatch = false;
          for (var i = 0; i < vals.length; i++) {
            if (panelMatchFn(item, vals[i])) { anyMatch = true; break; }
          }
          if (!anyMatch) return false;
        }
        return true;
      });

      // Check each bar button
      var activeWasHidden = false;
      root.querySelectorAll('.btn[data-filter]').forEach(function (btn) {
        var filter = btn.getAttribute('data-filter');
        if (filter === '*') return; // "Tutti/All" always visible

        var btnHasItems = matchingItems.some(function (item) {
          return cfg.barMatchFn(item, filter);
        });

        if (btnHasItems) {
          btn.style.display = '';
        } else {
          btn.style.display = 'none';
          if (btn.classList.contains('--active')) {
            btn.classList.remove('--active');
            activeWasHidden = true;
          }
        }
      });

      // If active bar button was hidden, fallback to "Tutti/All"
      if (activeWasHidden) {
        var allBtn = root.querySelector('.btn[data-filter="*"]');
        if (allBtn) allBtn.classList.add('--active');
      }

      if (cfg.onBarUpdate) {
        cfg.onBarUpdate(activeWasHidden);
      }
    }

    return {
      update: update
    };
  }

  return {
    create: create,
    hasClass: hasClass
  };

})();
